import { faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Group, Text } from '@mantine/core';
import { If, PermissionToggle, FilterSearch } from '@vision/ui/components';
import { DATA_GRID_DEFAULT_CURRENT_PAGE, DATA_GRID_DEFAULT_PER_PAGE } from '@vision/ui/constants';
import { useDataGridFilterHeadroom, useNavigationLinkMatched } from '@vision/ui/hooks';
import { DataGridParams, FilterSelections, Meta } from '@vision/ui/interfaces';
import {
  ensureArray,
  normalizeSelectionsWithMetaResponse,
  parseQueryObjectToSelections,
  parseSelectionsToQueryObject,
  processFilterSelections,
} from '@vision/ui/utils';
import clsx from 'clsx';
import { dequal } from 'dequal';
import React, { PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { DataGridFilters } from '../DataGridFilters';
import { useTranslation } from 'react-i18next';
import classes from './DataGridActionSlot.module.css';

interface DataGridActionSlotProps {
  actions: React.ReactNode;
  searchTooltip?: string;
  dataGridUrlParamsWithFilterSelections: DataGridParams;
  filterSelectionsDataGridUrlParams: Record<string, string>;
  meta: Meta;
  onRecordAdd?: VoidFunction;
  params: DataGridParams;
  recordAddButtonLabel?: string;
  recordAddButtonVisible?: boolean;
  updateDataGridUrlParams: (params: Partial<DataGridParams>) => void;
  onRecordAddPermission?: string;
  filterKey: string;
}

function _DataGridActionSlot({
  actions,
  searchTooltip,
  children,
  dataGridUrlParamsWithFilterSelections,
  filterSelectionsDataGridUrlParams,
  meta,
  onRecordAdd,
  params,
  recordAddButtonLabel,
  recordAddButtonVisible = true,
  updateDataGridUrlParams,
  onRecordAddPermission,
  filterKey,
}: PropsWithChildren<DataGridActionSlotProps>) {
  const { t } = useTranslation(['translation', 'data-grid']);

  const pinned = useDataGridFilterHeadroom();
  const { hasChildItems } = useNavigationLinkMatched();

  const tooltipLabel = t('data-grid:searchFields', {
    fields: ensureArray(meta?.generic_search_keys)
      .map((key) => t(`data-grid:searchFieldKeys.${key}`))
      .join(', '),
  });

  const filterSelectionsFromUrl = useMemo(() => {
    if (!meta) {
      return [];
    }

    const selections = normalizeSelectionsWithMetaResponse(
      meta,
      parseQueryObjectToSelections(filterSelectionsDataGridUrlParams),
    );

    return selections.length || dataGridUrlParamsWithFilterSelections.fc ? selections : processFilterSelections(meta);
  }, [dataGridUrlParamsWithFilterSelections.fc, filterSelectionsDataGridUrlParams, meta]);

  const handleUpdateFilterSelections = useCallback(
    (selections: FilterSelections[]) => {
      updateDataGridUrlParams({
        extraQueryValues: parseSelectionsToQueryObject(selections),
        fc: true, // used on useDataGridParams.ts
        page: DATA_GRID_DEFAULT_CURRENT_PAGE, // when filters changes, reset to page: 1
        per_page: params.per_page ?? DATA_GRID_DEFAULT_PER_PAGE,
      });
    },
    [params, updateDataGridUrlParams],
  );

  useEffect(() => {
    if (
      meta &&
      dataGridUrlParamsWithFilterSelections.fc &&
      !dequal(parseSelectionsToQueryObject(filterSelectionsFromUrl), filterSelectionsDataGridUrlParams)
    ) {
      handleUpdateFilterSelections(filterSelectionsFromUrl);
    }
  }, [
    dataGridUrlParamsWithFilterSelections.fc,
    filterSelectionsDataGridUrlParams,
    filterSelectionsFromUrl,
    handleUpdateFilterSelections,
    meta,
  ]);

  const handleSearch = useCallback(
    (value: string) => {
      updateDataGridUrlParams({
        search_string: value,
      });
    },
    [updateDataGridUrlParams],
  );

  const freeSearchTextQuery = useMemo(() => {
    return dataGridUrlParamsWithFilterSelections.search_string || '';
  }, [dataGridUrlParamsWithFilterSelections]);

  return (
    <>
      <Group
        justify="space-between"
        className={clsx(classes.dataGridActionSlot, {
          [classes.pinned]: pinned,
          [classes.withBottomBar]: pinned && hasChildItems,
        })}
        wrap="nowrap"
        data-testid="section-data-grid-actions"
      >
        <If value={meta?.filters?.length > 0}>
          <DataGridFilters
            filterSelectionsFromUrl={filterSelectionsFromUrl}
            onFilterSelectionChange={handleUpdateFilterSelections}
            filterKey={filterKey}
          />
        </If>

        <If value={!!meta || !!actions}>
          <Group gap={5} className={classes.items}>
            <If value={meta?.searchable}>
              <FilterSearch
                onSearch={handleSearch}
                searchText={freeSearchTextQuery}
                tooltipDisabled={!meta?.generic_search_keys?.length}
                tooltipLabel={searchTooltip || tooltipLabel}
              />
            </If>

            <If value={!!actions}>
              <div className={classes.items}>{actions}</div>
            </If>

            <If value={meta?.pagination?.total_count > 0 && recordAddButtonVisible}>
              <PermissionToggle value={onRecordAddPermission}>
                <Button
                  className={classes.addButton}
                  data-testid="button-data-grid-add"
                  color="blue.4"
                  variant="filled"
                  size="sm"
                  onClick={onRecordAdd}
                >
                  <Group gap={10}>
                    <FontAwesomeIcon icon={faCirclePlus} />
                    <Text className={classes.label}>{recordAddButtonLabel || t('add')}</Text>
                  </Group>
                </Button>
              </PermissionToggle>
            </If>
          </Group>
        </If>
      </Group>
      {children}
    </>
  );
}

export const DataGridActionSlot = React.memo(_DataGridActionSlot) as typeof _DataGridActionSlot;
